import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LogOut, Lock, DollarSign, ShoppingBag, Heart, Users, Mail, Eye, TrendingUp } from "lucide-react";
import { Wordmark } from "../components/Motion";
import { useAuth } from "../context/AuthContext";
import { adminStats, adminAnalytics, adminOrders, adminAccounts, adminMessages } from "../lib/api";

function formatErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const fmtDate = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

function AdminLogin() {
  const { login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const u = await login(form);
      if (u.role !== "admin") setErr("This account is not an admin.");
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-sm mx-auto px-4 sm:px-8 py-24 sm:py-32" data-testid="admin-login">
      <div className="inline-flex items-center justify-center w-14 h-14 border border-line mb-8"><Lock size={22} className="text-graytx" /></div>
      <h1 className="font-display font-extrabold text-4xl uppercase leading-[0.9]">Admin</h1>
      <p className="text-graytx mt-3 text-sm">Restricted area. Sign in with your admin credentials.</p>
      <form onSubmit={submit} className="mt-8 space-y-5">
        {[["email", "Email", "email"], ["password", "Password", "password"]].map(([k, l, t]) => (
          <div key={k}>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-graytx">{l}</label>
            <input required type={t} value={form[k]} onChange={set(k)} data-testid={`admin-${k}`}
              className="w-full bg-transparent border-b border-line focus:border-cream py-3 outline-none transition-colors duration-200" />
          </div>
        ))}
        {err && <p className="text-cs-red text-sm" data-testid="admin-error">{err}</p>}
        <button disabled={loading} data-testid="admin-login-btn"
          className="w-full bg-cream text-bg font-mono text-[12px] uppercase tracking-[0.2em] py-4 hover:bg-cs-yellow disabled:opacity-60 transition-colors duration-200">
          {loading ? "Signing in…" : "Enter Dashboard"}
        </button>
      </form>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="border border-line p-5" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
    <Icon size={16} className={accent} />
    <p className="font-display font-extrabold text-3xl mt-3 tabular-nums">{value}</p>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-graytx mt-1">{label}</p>
  </div>
);

function Dashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState("traffic");

  useEffect(() => {
    adminStats().then(setStats).catch(() => {});
    adminAnalytics().then(setAnalytics).catch(() => {});
    adminOrders().then(setOrders).catch(() => {});
    adminAccounts().then(setAccounts).catch(() => {});
    adminMessages().then(setMessages).catch(() => {});
  }, []);

  const TABS = [["traffic", "Traffic"], ["orders", `Orders (${orders.length})`], ["accounts", `Accounts (${accounts.length})`], ["messages", `Messages (${messages.length})`]];
  const maxDaily = Math.max(1, ...(analytics?.daily || []).map((d) => d.count));

  return (
    <div className="max-w-[1300px] mx-auto px-4 sm:px-8 py-12 sm:py-16" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cs-green">Admin · Covertswag HQ</p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl uppercase mt-2">Dashboard</h1>
        </div>
        <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-graytx hover:text-cs-red transition-colors duration-200"><LogOut size={14} /> Log out</button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
          <StatCard icon={DollarSign} label="Revenue" value={`$${stats.revenue.toFixed(0)}`} accent="text-cs-green" />
          <StatCard icon={ShoppingBag} label="Paid Orders" value={stats.orders} accent="text-cs-blue" />
          <StatCard icon={Heart} label="Donations" value={`$${stats.donation_total.toFixed(0)}`} accent="text-cs-red" />
          <StatCard icon={Users} label="Accounts" value={stats.accounts} accent="text-cs-yellow" />
        </div>
      )}

      <div className="flex gap-2 mt-12 border-b border-line">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-3 border-b-2 -mb-px transition-colors duration-200 ${tab === k ? "border-cream text-cream" : "border-transparent text-graytx hover:text-cream"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto no-scrollbar">
        {tab === "traffic" && (
          <div data-testid="traffic-panel">
            {!analytics ? (
              <p className="py-8 text-graytx font-mono text-sm">Loading traffic…</p>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <StatCard icon={Eye} label="Total Visits" value={analytics.total} accent="text-cs-blue" />
                  <StatCard icon={Users} label="Unique Visitors" value={analytics.uniques} accent="text-cs-yellow" />
                  <StatCard icon={TrendingUp} label="Visits (7d)" value={analytics.last7} accent="text-cs-green" />
                </div>

                <div className="mt-8 border border-line p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-graytx mb-6">Last 7 days</p>
                  <div className="flex items-end justify-between gap-2 h-40">
                    {analytics.daily.map((d) => (
                      <div key={d.date} className="flex-1 flex flex-col items-center gap-2" data-testid={`bar-${d.date}`}>
                        <span className="font-mono text-[10px] text-graytx tabular-nums">{d.count}</span>
                        <div className="w-full bg-cs-green/80" style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: d.count ? "3px" : "0" }} />
                        <span className="font-mono text-[9px] text-graytx">{d.date.slice(5)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-8">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-graytx mb-4">Top pages</p>
                  <div className="space-y-2" data-testid="top-pages">
                    {analytics.top_pages.map((p) => (
                      <div key={p.path} className="flex items-center justify-between border-b border-line py-2">
                        <span className="font-mono text-sm">{p.path}</span>
                        <span className="font-mono text-sm text-graytx tabular-nums">{p.count}</span>
                      </div>
                    ))}
                    {analytics.top_pages.length === 0 && <p className="py-6 text-graytx text-sm">No visits recorded yet.</p>}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        {tab === "orders" && (
          <table className="w-full text-sm" data-testid="orders-table">
            <thead><tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-graytx border-b border-line">
              <th className="text-left py-3 pr-4">Date</th><th className="text-left py-3 pr-4">Type</th><th className="text-left py-3 pr-4">Items</th><th className="text-left py-3 pr-4">Amount</th><th className="text-left py-3">Status</th></tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.session_id} className="border-b border-line">
                  <td className="py-3 pr-4 text-graytx whitespace-nowrap">{fmtDate(o.created_at)}</td>
                  <td className="py-3 pr-4">{o.type === "donation" ? "Donation" : "Order"}</td>
                  <td className="py-3 pr-4 text-graytx max-w-[280px] truncate">{(o.items || []).map((i) => `${i.product_id} (${i.size}) x${i.quantity}`).join(", ") || "—"}</td>
                  <td className="py-3 pr-4 font-mono">${(o.amount || 0).toFixed(0)}</td>
                  <td className="py-3"><span className={`font-mono text-[10px] uppercase tracking-widest ${o.payment_status === "paid" ? "text-cs-green" : "text-graytx"}`}>{o.payment_status}</span></td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={5} className="py-8 text-graytx">No orders yet.</td></tr>}
            </tbody>
          </table>
        )}
        {tab === "accounts" && (
          <table className="w-full text-sm" data-testid="accounts-table">
            <thead><tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-graytx border-b border-line">
              <th className="text-left py-3 pr-4">Name</th><th className="text-left py-3 pr-4">Email</th><th className="text-left py-3 pr-4">Code</th><th className="text-left py-3">Joined</th></tr></thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id} className="border-b border-line">
                  <td className="py-3 pr-4">{a.name}</td><td className="py-3 pr-4 text-graytx">{a.email}</td>
                  <td className="py-3 pr-4 font-mono text-cs-yellow">{a.discount_code || "—"}</td>
                  <td className="py-3 text-graytx whitespace-nowrap">{fmtDate(a.created_at)}</td>
                </tr>
              ))}
              {accounts.length === 0 && <tr><td colSpan={4} className="py-8 text-graytx">No accounts yet.</td></tr>}
            </tbody>
          </table>
        )}
        {tab === "messages" && (
          <div className="space-y-3" data-testid="messages-list">
            {messages.map((m) => (
              <div key={m.id} className="border border-line p-5">
                <div className="flex justify-between items-baseline">
                  <p className="font-display font-semibold">{m.name} <span className="text-graytx font-normal text-sm">· {m.email}</span></p>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-graytx">{fmtDate(m.created_at)}</span>
                </div>
                <p className="text-graytx text-sm mt-2 leading-relaxed">{m.message}</p>
              </div>
            ))}
            {messages.length === 0 && <p className="py-8 text-graytx">No messages yet.</p>}
          </div>
        )}
      </div>

      <div className="mt-16 flex justify-center"><Wordmark className="text-xl" /></div>
    </div>
  );
}

export default function Admin() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-[50vh] flex items-center justify-center font-mono text-graytx text-sm">Loading…</div>;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
      {user?.role === "admin" ? <Dashboard /> : <AdminLogin />}
    </motion.div>
  );
}
